/**
 * Agents Settings Page
 *
 * Hub for workspace agents, grouped by Doc 24 categories:
 *   Guide · Item shaping · Intelligence · Voices · AI Assists
 * Voices and AI Assists are special agent sections at the bottom — not
 * separate settings nav destinations (routes remain for deep links).
 *
 * Lives under People & Agents → Agents.
 */

import { Link } from 'react-router-dom';
import { Bot, ChevronRight, CheckCircle2, CircleSlash, PlayCircle, Clock, Sparkles, Mic2, Plus } from 'lucide-react';
import { useEffect, useCallback, useMemo } from 'react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useAgentRunStore } from '../../../stores/prioritization/agentRunStore';
import type { AgentBatchRunner } from '../../../stores/prioritization/agentRunStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useAgentSettingsStore } from '../../../stores/prioritization/agentSettingsStore';
import { useHarryStore } from '../../../stores/prioritization/harryStore';
import { useWesStore } from '../../../stores/prioritization/wesStore';
import { useGuideAgentStore } from '../../../stores/prioritization/guideAgentStore';
import { assistCapabilitiesFor } from '../../../lib/ai/useAiCapability';
import { useElevationAccess } from '../../../lib/elevationAccess';
import { isAiCapabilityEnabled } from '../../../lib/ai/aiAssistSettings';
import { useAiAssistSettingsStore } from '../../../stores/prioritization/aiAssistSettingsStore';
import { GuideAgentsSection } from './GuideAgentsSection';
import { AiAssistsCatalog } from './AiAssistsCatalog';
import { useAgentsAsUsersEnabled, VoiceBadge, useVoicesStore } from '../../../features/voices';
import type { CornerAssistant } from '../../../lib/harry/harryPrefs';
import { useCustomAgentStore } from '../../../stores/prioritization/customAgentStore';
import { AGENT_DEFINITIONS, definitionForCustomAgent, type AgentDefinition } from '../../../lib/ai/agentDefinitions';
import { describeAgent } from '../../../lib/ai/describeAgent';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useValueAiSettingsStore } from '../../../stores/prioritization/valueAiSettingsStore';
import { hasTemplateBatchRunner, templateBatchRunnerFor } from '../../../lib/agentTemplateRunners';
import { resolveTriggers } from '../../../components/agents/template/resolve';

function CornerAssistantSection() {
  const userId = useUserStore((s) => s.userId);
  const uiPrefs = useHarryStore((s) => s.uiPrefs);
  const setUiPrefs = useHarryStore((s) => s.setUiPrefs);
  const coFacilitatorAvailable = isAiCapabilityEnabled('co_facilitator', getCurrentWorkspaceId());
  const corner = uiPrefs.cornerAssistant;

  const select = (next: CornerAssistant) => {
    if (next === 'co_facilitator' && !coFacilitatorAvailable) return;
    setUiPrefs(userId, { cornerAssistant: next });
  };

  return (
    <section className="space-y-3">
      <div>
        <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] px-1">
          Corner assistant
        </p>
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] px-1 mt-0.5">
          One floating button in the lower-right — never both. The header icon always opens the Navigator.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => select('guides')}
          className={`card text-left p-4 transition-all ${
            corner === 'guides'
              ? 'border-[var(--slate)] ring-1 ring-indigo-200 dark:border-[var(--slate)]/60 dark:ring-indigo-800/40'
              : 'hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
          }`}
        >
          <p className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] m-0">Navigator</p>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1 mb-0">
            Say what you want to do; it finds where to do it, or where to start.
          </p>
          {corner === 'guides' && (
            <span className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-3 w-3" />
              Active corner
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => select('co_facilitator')}
          disabled={!coFacilitatorAvailable}
          className={`card text-left p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            corner === 'co_facilitator'
              ? 'border-[var(--slate)] ring-1 ring-indigo-200 dark:border-[var(--slate)]/60 dark:ring-indigo-800/40'
              : 'hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
          }`}
        >
          <p className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] m-0 flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-[var(--green)]" />
            Co-Facilitator
          </p>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1 mb-0">
            Methodology coach — how to think about prioritization, not product navigation.
          </p>
          {!coFacilitatorAvailable ? (
            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-2 mb-0">
              Enable Co-Facilitator under AI Assists to use this corner.
            </p>
          ) : corner === 'co_facilitator' ? (
            <span className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-3 w-3" />
              Active corner
            </span>
          ) : null}
        </button>
      </div>

      {/* Nested guide persona picker — always mounted; height + opacity animate in/out */}
      <div
        className="grid transition-[grid-template-rows,opacity] duration-[250ms] ease-in-out"
        style={{
          gridTemplateRows: corner === 'guides' ? '1fr' : '0fr',
          opacity: corner === 'guides' ? 1 : 0,
        }}
      >
        <div className="overflow-hidden min-h-0">
          <div className="border border-[var(--slate)] bg-[var(--slate-dim)]/40 dark:border-[var(--slate)]/40 dark:bg-[var(--slate)]/20 p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Which voice?
              </p>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                Plain, Harry or Wes — one voice for everyone in the workspace.
              </p>
            </div>
            <GuideAgentsSection inline />
          </div>
        </div>
      </div>
    </section>
  );
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return date.toLocaleDateString();
}

// ── Agent registry ─────────────────────────────────────────────────────────────

interface AgentDescriptor {
  id: string;
  name: string;
  tagline: string;
  description: string;
  /** Custom agents: the one sentence that reads its settings back, under the admin's own line. */
  readBack?: string;
  href: string;
  icon: React.ReactNode;
  statusLabel: (enabled: boolean) => string;
}

// ── Agent Card ────────────────────────────────────────────────────────────────

interface AgentCardProps {
  agent: AgentDescriptor;
  enabled: boolean | null;
  loading: boolean;
  lastRunAt?: string | null;
  runStatus?: string;
  onRunNow?: () => void;
}

/** Matches settings index/group pages: 1 → 2 → 3 columns. */
const AGENT_GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3';

function AgentCard({ agent, enabled, loading, lastRunAt, runStatus, onRunNow }: AgentCardProps) {
  const isRunning = runStatus === 'running';

  return (
    <div className="card flex h-full flex-col gap-3 p-4 hover:border-[var(--slate)] transition-all group">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 scale-90 origin-top-left">{agent.icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Link
              to={agent.href}
              className="text-base font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors no-underline line-clamp-2"
            >
              {agent.name}
            </Link>
            <Link to={agent.href} className="no-underline flex-shrink-0" tabIndex={-1}>
              <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] transition-colors" />
            </Link>
          </div>
          {!loading && enabled !== null && (
            <span
              className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
                enabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}
            >
              {enabled ? <CheckCircle2 className="h-3 w-3" /> : <CircleSlash className="h-3 w-3" />}
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-[var(--text-ter)] leading-snug">{agent.tagline}</p>
        {agent.description ? (
          <p className="text-sm text-[var(--text-ter)] leading-relaxed line-clamp-4">{agent.description}</p>
        ) : null}
        {agent.readBack ? (
          <p className="text-sm text-[var(--text-sec)] leading-relaxed line-clamp-3" data-testid="agent-card-read-back">{agent.readBack}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-auto pt-1">
        {lastRunAt && (
          <span className="flex items-center gap-1 text-[11px] text-[var(--text-ter)]">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(lastRunAt)}
          </span>
        )}
        {onRunNow && enabled && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRunNow();
            }}
            disabled={isRunning}
            className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 transition-colors"
          >
            <PlayCircle className="h-3.5 w-3.5" />
            {isRunning ? 'Running…' : 'Run now'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Intelligence agent card (self-contained: owns its config + run state) ───────


/** An agent built on the template: definition + agent_settings + the shared run store. */
function TemplateAgentCard({ def, workspaceId, userId }: {
  def: AgentDefinition;
  workspaceId: string | null;
  userId: string | null;
}) {
  const config = useAgentSettingsStore((s) => s.byAgent[def.agentType]?.config ?? null);
  const loading = useAgentSettingsStore((s) => s.byAgent[def.agentType]?.isLoading ?? false);
  const fetchConfig = useAgentSettingsStore((s) => s.fetchConfig);
  const runSlice = useAgentRunStore((s) => s.byAgent[def.agentType]);
  const startRun = useAgentRunStore((s) => s.startRun);
  const fetchRecentLogs = useAgentRunStore((s) => s.fetchRecentLogs);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchConfig(workspaceId, def.agentType);
    void fetchRecentLogs(workspaceId, def.agentType);
  }, [workspaceId, def.agentType, fetchConfig, fetchRecentLogs]);

  const batchRunner: AgentBatchRunner = useCallback(
    (wsId, scope, onProgress, signal) => templateBatchRunnerFor(def)(wsId, scope, onProgress, signal, userId),
    [def, userId],
  );

  const handleRunNow = () => {
    if (!workspaceId) return;
    void startRun({
      agentType: def.agentType,
      workspaceId,
      triggeredBy: 'manual',
      scope: { type: 'all_empty' },
      runByUserId: userId ?? null,
      batchRunner,
    });
  };

  // A custom agent's card keeps the admin's own "What it is for" line and, under it, reads the agent's
  // settings back in one sentence — the same sentence the creator showed.
  const attributes = useAttributesStore((s) => s.attributes);
  const valueAi = useValueAiSettingsStore((s) => s.settings);
  const loadValueAi = useValueAiSettingsStore((s) => s.load);
  useEffect(() => {
    if (workspaceId && def.kind === 'custom' && def.behaviorKind === 'estimate_value') void loadValueAi(workspaceId);
  }, [workspaceId, def.kind, def.behaviorKind, loadValueAi]);
  const readBack = useMemo(() => {
    if (def.kind !== 'custom') return undefined;
    const valueLabels = valueAi
      .filter((v) => v.mode !== 'off' && v.agentType === def.agentType)
      .map((v) => attributes.find((a) => a.key === v.attributeKey)?.label ?? v.attributeKey);
    return describeAgent(def, { settings: config, valueLabels });
  }, [def, config, valueAi, attributes]);

  const Icon = def.icon;
  return (
    <AgentCard
      agent={{
        id: def.agentType,
        name: def.name,
        tagline: def.tagline,
        description: def.description,
        readBack,
        href: def.settingsHref,
        icon: (
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center ${def.accent.iconBg} ${def.accent.iconText}`}>
            <Icon className="h-5 w-5" />
          </div>
        ),
        statusLabel: (e) => (e ? 'Enabled' : 'Disabled'),
      }}
      enabled={config?.enabled ?? false}
      loading={loading}
      lastRunAt={runSlice?.lastRunLog?.completedAt ?? null}
      runStatus={runSlice?.status}
      onRunNow={config?.enabled && hasTemplateBatchRunner(def) && resolveTriggers(def, config).runNow ? handleRunNow : undefined}
    />
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AgentsPage() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);

  // Guide (harry / wes configs loaded for voice settings sync)
  const fetchHarryConfig = useHarryStore((s) => s.fetchConfig);
  const fetchWesConfig = useWesStore((s) => s.fetchConfig);
  const fetchActiveGuide = useGuideAgentStore((s) => s.fetchActiveGuide);

  const fetchRecentLogs = useAgentRunStore((s) => s.fetchRecentLogs);

  const voicesEnabled = useAgentsAsUsersEnabled();
  const voices = useVoicesStore((s) => s.voices);
  const fetchVoices = useVoicesStore((s) => s.fetchVoices);

  const customAgents = useCustomAgentStore((s) => s.agents);
  const customAgentsWorkspace = useCustomAgentStore((s) => s.loadedWorkspaceId);
  const fetchCustomAgents = useCustomAgentStore((s) => s.fetchAgents);
  const customAgentDefs = useMemo(
    () => (customAgentsWorkspace === workspaceId ? customAgents.map(definitionForCustomAgent) : []),
    [customAgents, customAgentsWorkspace, workspaceId],
  );
  const canCreateAgents = useUserStore((s) => s.can)('admin.settings');

  // AI Assists
  const assistsSlice = useAiAssistSettingsStore((s) =>
    workspaceId ? s.byWorkspace[workspaceId] : undefined,
  );
  const saveAssistEnabled = useAiAssistSettingsStore((s) => s.saveEnabled);
  const savingAssistId = useAiAssistSettingsStore((s) => s.savingCapabilityId);
  const fetchAllAssists = useAiAssistSettingsStore((s) => s.fetchAll);

  // Counts what the assists catalog lists: an assist with an Elevation level
  // only for people at that level.
  const assists = assistCapabilitiesFor(useElevationAccess());
  const enabledAssistCount = assists.filter((c) =>
    isAiCapabilityEnabled(c.id, workspaceId),
  ).length;

  useEffect(() => {
    if (workspaceId) {
      void fetchHarryConfig(workspaceId, userId);
      void fetchWesConfig(workspaceId);
      void fetchActiveGuide(workspaceId);
      void fetchAllAssists(workspaceId);
    }
  }, [workspaceId, fetchHarryConfig, fetchWesConfig, fetchActiveGuide, fetchRecentLogs, fetchAllAssists, userId]);

  useEffect(() => {
    if (voicesEnabled && workspaceId) void fetchVoices(workspaceId);
  }, [voicesEnabled, workspaceId, fetchVoices]);

  useEffect(() => {
    if (workspaceId) void fetchCustomAgents(workspaceId);
  }, [workspaceId, fetchCustomAgents]);

  const handleAssistToggle = useCallback(
    (capabilityId: string, enabled: boolean) => {
      if (!workspaceId) return;
      void saveAssistEnabled(workspaceId, capabilityId, enabled, userId ?? undefined);
    },
    [workspaceId, userId, saveAssistEnabled],
  );

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<Bot className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Agents"
        subtitle="Workspace agents by category — guide, item work, intelligence, Voices, and AI Assists. Voice, never authority."
      />

      {/* ── Corner assistant + guide persona (nested) ─────────────────────── */}
      <CornerAssistantSection />

      {/* ── Item shaping (Doc 24 §4.1) ─────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] px-1">
            Item work
          </p>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] px-1 mt-0.5">
            Classify, dedupe, intake, relate, and estimate items — batch-capable workers with run logs.
          </p>
        </div>

        <div className={AGENT_GRID}>
          {Object.values(AGENT_DEFINITIONS).filter((def) => def.behaviorKind !== 'find_issues').map((def) => (
            <TemplateAgentCard key={def.agentType} def={def} workspaceId={workspaceId} userId={userId} />
          ))}
        </div>
      </section>

      {/* ── Intelligence (Doc 24 §4.1) ─────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] px-1">
            Intelligence
          </p>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] px-1 mt-0.5">
            Read process data and surface fragility, strategy gaps, and leverage — findings first, narration optional.
          </p>
        </div>

        <div className={AGENT_GRID}>
          {Object.values(AGENT_DEFINITIONS).filter((def) => def.behaviorKind === 'find_issues').map((def) => (
            <TemplateAgentCard key={def.agentType} def={def} workspaceId={workspaceId} userId={userId} />
          ))}
        </div>
      </section>

      {/* ── Custom agents (spec §2 J): made by administrators from a built-in type ── */}
      <section className="space-y-3">
        <div className="flex items-start justify-between gap-3 px-1">
          <div>
            <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Custom agents
            </p>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              Made by your workspace from a built-in type, with your own name, documents and words. They propose; people decide.
            </p>
          </div>
          {canCreateAgents && (
            <Link
              to="/prioritization/settings/custom-agents"
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] no-underline whitespace-nowrap"
            >
              <Plus className="h-4 w-4" /> Create an agent
            </Link>
          )}
        </div>

        {customAgentDefs.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] px-1">
            {canCreateAgents ? 'None yet.' : 'None yet. A workspace administrator can make one.'}
          </p>
        ) : (
          <div className={AGENT_GRID}>
            {customAgentDefs.map((def) => (
              <TemplateAgentCard key={def.agentType} def={def} workspaceId={workspaceId} userId={userId} />
            ))}
          </div>
        )}
      </section>

      {/* ── Voices (participant standing; module + flag only) ─────────────── */}
      {voicesEnabled && (
        <section className="space-y-3">
          <div className="flex items-start justify-between gap-3 px-1">
            <div>
              <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Voices
              </p>
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
                Participant-standing agents invited into async sessions as context providers.
              </p>
            </div>
            <Link
              to="/prioritization/settings/voices"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap"
            >
              Manage all
            </Link>
          </div>

          <div className={AGENT_GRID}>
            {voices.length === 0 ? (
              <Link
                to="/prioritization/settings/voices"
                className="card flex h-full flex-col gap-2 p-4 no-underline hover:border-[var(--rule)] transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-electric-mustard-100 text-electric-mustard-700">
                    <Mic2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-[var(--text-pri)] m-0">No Voices yet</p>
                    <p className="text-sm text-[var(--text-ter)] m-0 mt-0.5 leading-relaxed line-clamp-4">
                    Add a Customer Voice or Revenue Voice so constituency input can join async
                    sessions as constituency context — never as votes or authority.
                  </p>
                  </div>
                </div>
              </Link>
            ) : (
              voices.map((v) => {
                const corpusTagline =
                  v.corpusKind === 'support_tickets'
                    ? 'Support-ticket corpus'
                    : v.corpusKind === 'crm_pipeline'
                      ? 'CRM / sales pipeline corpus'
                      : 'Constituency Voice';
                return (
                  <Link
                    key={v.id}
                    to={`/prioritization/settings/voices/${v.id}`}
                    className="card flex h-full flex-col gap-2 p-4 no-underline hover:border-[var(--rule)] transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-electric-mustard-100 text-electric-mustard-700">
                        <Mic2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-base font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors line-clamp-2">
                            {v.displayName}
                          </span>
                          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 transition-colors" />
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <VoiceBadge label={v.badgeLabel} />
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
                              v.status === 'active'
                                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                                : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                            }`}
                          >
                            {v.status === 'active' ? (
                              <CheckCircle2 className="h-3 w-3" />
                            ) : (
                              <CircleSlash className="h-3 w-3" />
                            )}
                            {v.status === 'active' ? 'Active' : 'Suspended'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 flex-1">
                      <p className="text-sm font-medium text-[var(--text-ter)] leading-snug">{corpusTagline}</p>
                      <p className="text-sm text-[var(--text-ter)] leading-relaxed line-clamp-4">
                        {v.description?.trim()
                          || 'Participant-standing agent invited into sessions for constituency context. Voice, never authority.'}
                      </p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </section>
      )}

      {/* ── AI Assists (inline / on-demand; full catalog with toggles) ─────── */}
      <section className="space-y-4">
        <div className="flex items-start justify-between gap-3 px-1">
          <div>
            <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              AI Assists
            </p>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              Inline helpers, narrators, and advisors — on-demand assists, not batch workers.{' '}
              {!assistsSlice?.isLoading && (
                <span className="text-[var(--text-ter)]">
                  {enabledAssistCount} of {assists.length} enabled.
                </span>
              )}
            </p>
          </div>
          <Link
            to="/prioritization/settings/ai-assists"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap"
          >
            Open full page
          </Link>
        </div>

        <AiAssistsCatalog
          workspaceId={workspaceId}
          savingCapabilityId={savingAssistId}
          onToggle={handleAssistToggle}
          compactTier
        />
      </section>

      <div className="card bg-[var(--surface)] border-[var(--rule)]">
        <p className="text-sm text-[var(--text-ter)] leading-relaxed">
          <strong className="text-[var(--text-sec)]">Voice, never authority.</strong>{' '}
          Worker agents propose findings for human confirmation. Voices contribute
          constituency context in sessions. Authority-lane transitions stay with accountable humans.
        </p>
      </div>
    </div>
  );
}
